import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import PageMeta from '@/components/base/PageMeta';
import { articleDetails, defaultArticleContent } from '@/mocks/articleDetailData';
import { allArticles } from '@/mocks/articleData';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const fallbackArticle = Object.values(articleDetails)[0];
  const article = id ? (articleDetails[id] || fallbackArticle) : fallbackArticle;

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-20 md:pt-24 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <i className="ri-article-line text-5xl text-foreground-300 mb-4 block"></i>
            <p className="text-foreground-500 text-lg">文章不存在或已被删除</p>
            <Link to="/" className="mt-4 inline-block text-primary-500 hover:text-primary-600 text-sm font-medium cursor-pointer">
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const relatedArticles = article.relatedIds
    .map((rid) => allArticles.find((a) => a.id === rid))
    .filter(Boolean);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'http://my783e.cn/' },
      { '@type': 'ListItem', position: 2, name: article.category, item: 'http://my783e.cn/category/all' },
      { '@type': 'ListItem', position: 3, name: article.title, item: `http://my783e.cn/article/${article.id}` },
    ],
  };

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.summary,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    image: article.image,
    publisher: {
      '@type': 'Organization',
      name: '吃瓜网官网',
      url: 'http://my783e.cn/',
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `http://my783e.cn/article/${article.id}`,
    },
  };

  return (
    <>
      <PageMeta
        title={`${article.title} — 吃瓜网官网`}
        description={article.summary}
        keywords={`${article.category},吃瓜网官网,吃瓜爆料,51吃瓜网官网,91吃瓜网官网,黑料吃瓜网官网,在线吃瓜`}
        canonicalUrl={`http://my783e.cn/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        schemaData={[breadcrumbSchema, articleSchema]}
      />

      <Navbar />

      <main>
        {/* Quote Hero */}
        <section className="relative w-full bg-foreground-950 overflow-hidden">
          <div className="max-w-6xl mx-auto px-4 md:px-6 pt-24 md:pt-28 pb-12 md:pb-16">
            <div className="flex flex-col lg:flex-row items-start gap-8 md:gap-12">
              <div className="w-full lg:w-[55%]">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                  <span className="text-sm font-semibold text-foreground-400">{article.category}</span>
                </div>
                <blockquote className="text-xl md:text-2xl lg:text-3xl font-heading text-background-50 leading-relaxed mb-5">
                  <span className="text-primary-400 text-4xl md:text-5xl leading-none">&ldquo;</span>
                  {article.quote}
                </blockquote>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-bold text-background-50">{article.author}</span>
                  <span className="px-2.5 py-0.5 rounded-full bg-accent-500/20 text-accent-400 text-xs font-medium">
                    <i className="ri-verified-badge-line mr-1"></i>
                    吃瓜网编辑
                  </span>
                  <span className="px-2.5 py-0.5 rounded-full bg-primary-500/20 text-primary-400 text-xs font-medium">
                    {article.tag}
                  </span>
                </div>
              </div>
              <div className="w-full lg:w-[45%]">
                <div className="rounded-2xl overflow-hidden aspect-[4/3]">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={article.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Breadcrumb + Article Content */}
        <section className="py-8 md:py-12 px-4 md:px-6 bg-background-50">
          <div className="max-w-4xl mx-auto">
            <nav className="flex items-center gap-2 text-xs md:text-sm text-foreground-500 mb-8" aria-label="面包屑导航">
              <Link to="/" className="hover:text-primary-500 whitespace-nowrap cursor-pointer transition-colors">首页</Link>
              <i className="ri-arrow-right-s-line"></i>
              <Link to="/category/all" className="hover:text-primary-500 whitespace-nowrap cursor-pointer transition-colors">{article.category}</Link>
              <i className="ri-arrow-right-s-line"></i>
              <span className="text-foreground-700 line-clamp-1">{article.title}</span>
            </nav>

            <header className="mb-8">
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-3">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  {article.date}
                </span>
                <span className="bg-secondary-100 text-secondary-700 px-2 py-0.5 rounded-md text-xs">
                  {article.category}
                </span>
              </div>
            </header>

            <article
              className="prose prose-lg max-w-none
                prose-headings:font-heading prose-headings:text-foreground-950
                prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
                prose-strong:text-foreground-950
                [&_p]:text-base [&_p]:md:text-lg"
              dangerouslySetInnerHTML={{ __html: article.content || defaultArticleContent }}
            />
          </div>
        </section>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-10 md:py-14 px-4 md:px-6 bg-background-100">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-end justify-between mb-8">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  相关爆料
                </h2>
                <Link
                  to="/category/all"
                  className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center gap-1"
                >
                  更多
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
                {relatedArticles.map((ra) => ra && (
                  <Link
                    key={ra.id}
                    to="/category/all"
                    className="group block rounded-xl overflow-hidden bg-background-50 hover:bg-background-200/70 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative aspect-[4/3] overflow-hidden">
                      <img
                        src={ra.image}
                        alt={ra.title}
                        title={ra.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute bottom-0 left-0 right-0 p-3 bg-gradient-to-t from-black/70 to-transparent">
                        <h3 className="text-sm font-semibold text-background-50 leading-snug line-clamp-2">
                          {ra.title}
                        </h3>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}

function getCategorySlug(categoryName: string): string {
  const map: Record<string, string> = {
    '明星黑料': 'mingxing-heiliao',
    '网红塌房': 'wanghong-tafang',
    '博主反差': 'bozhu-fancha',
    '每日爆料': 'meiri-baoliao',
  };
  return map[categoryName] || 'all';
}